import { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { Export, Eye } from '@phosphor-icons/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { getGroupedBuyersSuppliers } from '../../../../redux/services/buyersService';
import { DillTable, DillButton, type DillColumn } from '../../../general/Dill';

interface OpenInvoiceReportRow {
  key: string;
  invoiceId: string;
  docNumber: string;
  supplierName: string;
  status: string;
  dueDate: string;
  poNumber: string;
  terms: string;
  jobName: string;
  amount: number;
  balance: number;
}

const STATUS_LABELS: Record<string, string> = {
  PAST_DUE: 'Past Due',
  CURRENT_DUE: 'Current',
  THIS_WEEK: 'Due This Week',
  PAST_SIGNED: 'Past Signed',
  PENDING_PAY: 'Pending Payment',
  OPEN: 'Open',
  PAID: 'Paid',
};

const STATUS_STYLES: Record<string, string> = {
  PAST_DUE: 'bg-red-50 text-red-600',
  CURRENT_DUE: 'bg-blue-50 text-blue-600',
  THIS_WEEK: 'bg-amber-50 text-amber-600',
  PENDING_PAY: 'bg-purple-50 text-purple-600',
};

const COLUMN_LABELS: Record<string, string> = {
  docNumber: 'Invoice #',
  supplierName: 'Supplier',
  status: 'Status',
  dueDate: 'Due Date',
  poNumber: 'PO Number',
  terms: 'Terms',
  jobName: 'Job',
  amount: 'Invoice Amount',
  balance: 'Balance Due',
};

const textColumn = (
  key: keyof OpenInvoiceReportRow,
  label: string,
  options?: { defaultHidden?: boolean; width?: number }
): DillColumn<OpenInvoiceReportRow> => ({
  key,
  label,
  width: options?.width ?? 140,
  sortable: true,
  filterable: true,
  toggleable: true,
  defaultHidden: options?.defaultHidden,
  render: (value: string) => <div className="text-xs text-gray-500">{value || '—'}</div>,
});

const numericColumn = (
  key: keyof OpenInvoiceReportRow,
  label: string,
  width = 150
): DillColumn<OpenInvoiceReportRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  numeric: true,
  toggleable: true,
  headerAlign: 'right',
  render: (value: number) => {
    if (value === 0 || value == null) {
      return <div className="text-xs text-gray-300 text-right">—</div>;
    }
    return (
      <div className="text-xs font-medium text-right tabular-nums text-gray-700">
        {value < 0 ? '-' : ''}$
        {Math.abs(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    );
  },
});

const getColumns = (hasJobs: boolean): DillColumn<OpenInvoiceReportRow>[] => [
  textColumn('docNumber', 'Invoice #', { width: 130 }),
  textColumn('supplierName', 'Supplier', { width: 180 }),
  {
    key: 'status',
    label: 'Status',
    width: 130,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-500">—</div>;
      return (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${
            STATUS_STYLES[value] ?? 'bg-gray-50 text-gray-600'
          }`}>
          {STATUS_LABELS[value] ?? value}
        </span>
      );
    },
  },
  {
    key: 'dueDate',
    label: 'Due Date',
    width: 120,
    sortable: true,
    filterable: true,
    filterType: 'dateRange',
    toggleable: true,
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-500">—</div>;
      return <div className="text-xs text-gray-500">{moment(value).format('MM/DD/YYYY')}</div>;
    },
  },
  textColumn('poNumber', 'PO Number', { width: 130 }),
  textColumn('terms', 'Terms', { width: 110 }),
  textColumn('jobName', 'Job', { defaultHidden: !hasJobs, width: 160 }),
  numericColumn('amount', 'Invoice Amount', 140),
  numericColumn('balance', 'Balance Due', 140),
];

const OpenInvoicesReport = () => {
  const dispatch = useAppDispatch();
  const [selectedMainBuyerId] = useParamMainBuyerId();

  const { selectedBuyerSuppliers, loadingList } = useAppSelector((state) => state.buyers);

  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);

  const exportRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  const isLoading = loadingList.includes('getGroupedBuyersSuppliers');

  useEffect(() => {
    if (!selectedMainBuyerId) return;
    // weeksLimit only bounds the (unused here) paid-invoices lookback - 2 matches the value
    // most other buyer-portal pages already pass, to avoid needlessly invalidating their cache.
    dispatch(getGroupedBuyersSuppliers({ mainBuyerId: selectedMainBuyerId, weeksLimit: 2 }));
  }, [selectedMainBuyerId, dispatch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setIsExportOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(e.target as Node)) {
        setIsColumnsOpen(false);
      }
    };
    if (isExportOpen || isColumnsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExportOpen, isColumnsOpen]);

  const reportRows: OpenInvoiceReportRow[] = useMemo(() => {
    const rows: OpenInvoiceReportRow[] = [];
    (selectedBuyerSuppliers ?? []).forEach((supplierForBuyer) => {
      const supplierName = (supplierForBuyer as any).name ?? '';
      const invoices = [
        ...((supplierForBuyer as any).invoices?.pastDueInvoices?.invoices ?? []),
        ...((supplierForBuyer as any).invoices?.currentInvoices?.invoices ?? []),
      ];
      invoices.forEach((inv: any) => {
        rows.push({
          key: inv.id ?? `${supplierForBuyer.id}-${inv.docNumber}`,
          invoiceId: inv.id ?? '',
          docNumber: inv.docNumber ?? '',
          supplierName,
          status: inv.status ?? '',
          dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString() : '',
          poNumber: inv.poNumber ?? '',
          terms: inv.terms ?? '',
          jobName: inv.quickBooksJob?.jobName ?? '',
          amount: inv.amount ?? 0,
          balance: inv.balance ?? 0,
        });
      });
    });
    return rows;
  }, [selectedBuyerSuppliers]);

  const hasJobs = useMemo(() => reportRows.some((r) => !!r.jobName), [reportRows]);
  const allColumns = useMemo(() => getColumns(hasJobs), [hasJobs]);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key))
  );

  useEffect(() => {
    setHiddenCols((prev) => {
      if (hasJobs && prev.has('jobName')) {
        const next = new Set(prev);
        next.delete('jobName');
        return next;
      }
      if (!hasJobs && !prev.has('jobName')) {
        return new Set([...prev, 'jobName']);
      }
      return prev;
    });
  }, [hasJobs]);

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c.key)),
    [allColumns, hiddenCols]
  );

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const visibleCount = allColumns.filter((c) => !next.has(c.key)).length;
        if (visibleCount <= 3) return prev;
        next.add(key);
      }
      return next;
    });
  };

  const totalBalanceDue = useMemo(
    () => reportRows.reduce((sum, r) => sum + r.balance, 0),
    [reportRows]
  );

  const exportRows = () =>
    reportRows.map((row) => {
      const exported: Record<string, any> = {};
      visibleColumns.forEach((col) => {
        if (col.key === 'key') return;
        exported[COLUMN_LABELS[col.key] ?? col.key] = (row as any)[col.key];
      });
      return exported;
    });

  const handleExportXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Open Invoices Report');
    XLSX.writeFile(wb, 'buyer-open-invoices-report.xlsx');
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    const csv = Papa.unparse(exportRows());
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'buyer-open-invoices-report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsExportOpen(false);
  };

  const columnsDropdown = (
    <div className="relative" ref={columnsRef}>
      <DillButton
        label="Columns"
        onClick={() => setIsColumnsOpen(!isColumnsOpen)}
        icon={<Eye size={14} />}
        variant="ghost"
      />
      {isColumnsOpen && (
        <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-56 max-h-[400px] overflow-y-auto">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 flex items-center justify-between">
            <span>Show/Hide Columns</span>
            <button
              onClick={() => setHiddenCols(new Set())}
              className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal">
              Select All
            </button>
          </div>
          {allColumns.map((col) => {
            const isVisible = !hiddenCols.has(col.key);
            const canHide = isVisible && visibleColumns.length <= 3;
            return (
              <label
                key={col.key}
                className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                  canHide ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                }`}>
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={() => toggleColumn(col.key)}
                  disabled={canHide}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100/80 disabled:opacity-50"
                />
                <span className="flex-1">{col.label}</span>
                {canHide && <span className="text-[10px] text-gray-400">Min 3 required</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );

  const exportDropdown = (
    <div
      className="relative"
      ref={exportRef}
      title={isLoading ? 'Still loading open invoices…' : undefined}>
      <DillButton
        label="Export"
        onClick={() => setIsExportOpen(!isExportOpen)}
        icon={<Export size={14} />}
        variant="primary"
        isDisabled={isLoading}
      />
      {isExportOpen && (
        <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
          <button
            onClick={handleExportXlsx}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
            Export as XLSX
          </button>
          <button
            onClick={handleExportCsv}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
            Export as CSV
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 px-4 sm:px-6 pt-4 sm:pt-6 pb-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-800">Open Invoices Report</h2>
            <p className="text-xs text-gray-500 mt-1">
              Every past-due and currently-due invoice across all your suppliers, in one place.
            </p>
          </div>
        </div>

        {/* Summary bar */}
        {reportRows.length > 0 && (
          <div className="mx-4 sm:mx-6 mb-4 px-4 py-3 bg-blue-50 rounded-lg flex items-center gap-4 sm:gap-6">
            <div>
              <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">
                Open Invoices
              </p>
              <p className="text-sm font-semibold text-blue-800">{reportRows.length}</p>
            </div>
            <div className="w-px h-8 bg-blue-200" />
            <div>
              <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">
                Total Balance Due
              </p>
              <p className="text-sm font-semibold text-blue-800">
                $
                {totalBalanceDue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="px-2 sm:px-4 pb-4 h-[calc(100vh-290px)]">
          <DillTable<OpenInvoiceReportRow>
            columns={visibleColumns}
            data={reportRows}
            rowKey={(r) => r.key}
            verticalDividers
            search
            searchPlaceholder="Search open invoices…"
            pagination
            pageSize={25}
            rowHeight={35}
            scrollX
            showActiveFilters
            striped={false}
            // maxHeight="calc(100vh - 300px)"
            loading={isLoading}
            loadingText="Loading open invoices..."
            emptyMessage="No open invoices match your filters"
            showCount={false}
            toolbarRight={
              <div className="flex gap-2">
                {columnsDropdown}
                {exportDropdown}
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default OpenInvoicesReport;
